import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Users, ArrowLeftRight, DollarSign, TrendingUp } from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';

const BankerDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/dashboard');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Dashboard Administrativo</h1>
          <p className="text-muted-foreground mt-2">Visão geral das operações</p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="banker-dashboard">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Dashboard Administrativo</h1>
        <p className="text-muted-foreground mt-2">Visão geral das operações</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card className="relative overflow-hidden border-border/50 bg-gradient-to-br from-background to-muted/20">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total de Clientes</CardTitle>
            <Users className="h-4 w-4 text-accent" strokeWidth={1.5} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono tracking-tight" data-testid="total-customers">
              {stats?.total_customers || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.active_customers || 0} ativos
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border/50">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Transações</CardTitle>
            <ArrowLeftRight className="h-4 w-4 text-success" strokeWidth={1.5} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono tracking-tight" data-testid="total-transactions">
              {stats?.total_transactions || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Volume: {formatCurrency(stats?.transaction_volume || 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border/50">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Empréstimos Ativos</CardTitle>
            <DollarSign className="h-4 w-4 text-warning" strokeWidth={1.5} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono tracking-tight" data-testid="active-loans">
              {stats?.active_loans || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Volume: {formatCurrency(stats?.loan_volume || 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border/50">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Crescimento</CardTitle>
            <TrendingUp className="h-4 w-4 text-success" strokeWidth={1.5} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono tracking-tight">
              +12.5%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              vs. mês anterior
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle>Operações Recentes</CardTitle>
            <CardDescription>Atividade do sistema</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-border/50">
                <div>
                  <p className="font-medium">Novas Contas Criadas</p>
                  <p className="text-sm text-muted-foreground">Hoje</p>
                </div>
                <span className="text-2xl font-bold">{stats?.recent_activity?.new_customers_today || 0}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-border/50">
                <div>
                  <p className="font-medium">Transações Processadas</p>
                  <p className="text-sm text-muted-foreground">Hoje</p>
                </div>
                <span className="text-2xl font-bold">{stats?.recent_activity?.transactions_today || 0}</span>
              </div>
              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium">Empréstimos Aprovados</p>
                  <p className="text-sm text-muted-foreground">Hoje</p>
                </div>
                <span className="text-2xl font-bold">{stats?.recent_activity?.loans_approved_today || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle>Resumo Financeiro</CardTitle>
            <CardDescription>Visão consolidada</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="rounded-lg border border-border/50 p-4">
                <p className="text-sm text-muted-foreground mb-2">Volume Total Transacionado</p>
                <p className="text-2xl font-bold font-mono">
                  {formatCurrency(stats?.transaction_volume || 0)}
                </p>
              </div>
              <div className="rounded-lg border border-border/50 p-4">
                <p className="text-sm text-muted-foreground mb-2">Carteira de Empréstimos</p>
                <p className="text-2xl font-bold font-mono">
                  {formatCurrency(stats?.loan_volume || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BankerDashboard;